import React, { useContext, useMemo, useState } from "react";
import { AuthContext } from "../../../auth/authContext";
import { UserRole } from "../../../enums/userRoles";
import { Pivot, PivotSetupForm } from "../../../dtos/pivot";
import { usePivots } from "../../../hooks/usePivots";
import { useMessage } from "../../messageService";
import { useConfirmDialog } from "../../confirmDialogService";
import MaterialTable, { HeaderCells } from "../../shared/materialTable";
import { AddPivotModal, pivotToSetupForm } from "../../pivotPage/addPivotModal";

const PivotsPage = () => {
  const { role } = useContext(AuthContext);

  return (
    <div>
      {role === UserRole.User ? (
        <div>You do not have permissions to view this page.</div>
      ) : (
        <PivotsTable />
      )}
    </div>
  );
};

const PivotsTable = () => {
  const { pivots, addPivot, updatePivot, removePivot } = usePivots();
  const { showMessage } = useMessage();
  const { showConfirmDialog } = useConfirmDialog();
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [selectedPivot, setSelectedPivot] = useState<Pivot | null>(null);
  const addPivotQuery = new URLSearchParams(window.location.search).get("add");

  React.useEffect(() => {
    if (addPivotQuery === "true") {
      setShowAddEdit(true);
    }
  }, [addPivotQuery]);

  const initialForm = useMemo(
    () => (selectedPivot ? pivotToSetupForm(selectedPivot) : null),
    [selectedPivot]
  );

  const addUpdatePivot = (pivot: Pivot | null) => {
    setSelectedPivot(pivot);
    setShowAddEdit(true);
  };

  const finishOperation = () => {
    setShowAddEdit(false);
    setSelectedPivot(null);
  };

  const handleSave = (form: PivotSetupForm) => {
    if (selectedPivot) {
      updatePivot({
        ...selectedPivot,
        name: form.name,
        serial: form.serial,
        length: form.length_ft,
        description: form.description,
        lat: form.center_lat ?? undefined,
        lon: form.center_lon ?? undefined,
        pivot_type: form.pivot_type,
        travel_direction: form.travel_direction,
        speed_percent: form.speed_percent,
        limit_angle_a: form.limit_angle_a,
        limit_angle_b: form.limit_angle_b,
        limit_a_action: form.limit_a_action,
        limit_b_action: form.limit_b_action,
        end_gun_enabled: form.end_gun_enabled,
        end_gun_sections: form.end_gun_sections,
        pump_ids: form.pump_ids,
        valve_ids: form.valve_ids
      });
      showMessage("Pivot updated");
    } else {
      addPivot(form);
      showMessage("Pivot created");
    }

    finishOperation();
  };

  const tryDeletePivot = (pivot: Pivot) => {
    showConfirmDialog(
      `Are you sure you want to delete pivot${pivot.name ? " " + pivot.name : ""}?`
    ).then(confirmed => {
      if (!confirmed) {
        return;
      }

      removePivot(pivot.id);
      showMessage("Pivot deleted");
      finishOperation();
    });
  };

  const headCells: HeaderCells<Pivot>[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: "Name",
      accessor: row => row.name
    },
    {
      id: "serial",
      numeric: false,
      disablePadding: false,
      label: "Serial No",
      accessor: row => row.serial
    },
    {
      id: "length",
      numeric: false,
      disablePadding: false,
      label: "Length",
      accessor: row => `${row.length} ft`
    },
    {
      id: "pivot_type",
      numeric: false,
      disablePadding: false,
      label: "Type",
      accessor: row => (row.pivot_type === "partial" ? "Partial Circle" : "Full Circle")
    }
  ];

  return (
    <>
      <MaterialTable
        title="Pivots"
        headerCells={headCells}
        rows={pivots}
        defaultRowsPerPage={5}
        showAddButton={true}
        idPropertyAccessor={row => row.id}
        handleAddOrUpdate={addUpdatePivot}
        handleDelete={tryDeletePivot}
      />
      <AddPivotModal
        isVisible={showAddEdit}
        onSave={handleSave}
        close={finishOperation}
        initialForm={initialForm}
        title={selectedPivot ? "Edit Pivot" : "Add Pivot"}
        submitLabel={selectedPivot ? "Save Pivot" : "Create Pivot"}
      />
    </>
  );
};

export default PivotsPage;
